import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import { getExceededThresholdDetails } from '@app/services/printReport';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@app/components';
import { base64Decode } from '@app/helpers/base64url';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import fileDownload from 'js-file-download';

const ExceededThresholdDetails = () => {
    const { encodedId } = useParams();
    const [franchiseId, setFranchiseId] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('student_id');
    const [sortDirection, setSortDirection] = useState('DESC');
    const permissions = useSelector((state: any) => state.auth.permissions);
    const canAccess = permissions?.['print-summary']?.['exceeded-threshold'];
    const navigate = useNavigate();
    const franchiseData = useSelector((state: any) => state.printSummary.franchise);

    useEffect(() => {
        if (!canAccess) {
            navigate('/');
            toast.error('You do not have permission to access this page');
        }
    }, [canAccess, navigate]);

    useEffect(() => {
        if (encodedId) {
            try {
                const decoded = base64Decode(encodedId);
                const [fId, m, y] = decoded.split('-');
                setFranchiseId(fId);
                setMonth(m);
                setYear(y);
            } catch (error) {
                toast.error('Invalid URL parameters');
                navigate('/exceeded-print-threshold');
            }
        }
    }, [encodedId, navigate]);

    useEffect(() => {
        if (canAccess && franchiseId && month && year) {
            fetchDetails(currentPage, perPage, sortField, sortDirection);
        }
    }, [currentPage, perPage, sortField, sortDirection, canAccess, franchiseId, month, year]);

    const fetchDetails = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const resp = await getExceededThresholdDetails(franchiseId, month, year, sortField, sortDirection, page, perPage);
            if (resp.success === true) {
                setData(resp.collections || []);
                // Calculate total rows from all students across all subjects
                const totalStudents = (resp.collections || []).reduce((sum, subject) => 
                    sum + (subject.students?.length || 0), 0
                );
                setTotalRows(totalStudents);
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg || 'Failed to fetch details');
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection.toUpperCase());
    };

    const backToList = (e) => {
        e.preventDefault();
        navigate('/exceeded-print-threshold');
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            
            if (!totalRows || totalRows === 0) {
                setLoading(false);
                toast.error('No records found to export');
                return;
            }

            // Fetch all records using the total count from state
            const resp = await getExceededThresholdDetails(franchiseId, month, year, sortField, sortDirection, 1, totalRows);
            
            if (resp.success === true) {
                setLoading(false);
                
                if (!resp.collections || resp.collections.length === 0) {
                    toast.error('No records found to export');
                    return;
                }

                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Exceeded Threshold Details");
                
                // Add title row
                worksheet.mergeCells('A1:H1');
                const titleRow = worksheet.getRow(1);
                titleRow.getCell(1).value = `Exceeded Threshold Details - ${franchiseData?.name || 'Franchise'} (${getMonthName(month)} ${year})`;
                titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
                titleRow.getCell(1).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF2C3E50' }
                };
                titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
                titleRow.height = 30;

                let currentRow = 2;

                resp.collections.forEach((subject, subjectIndex) => {
                    // Subject header row with merged cells
                    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
                    const subjectHeaderRow = worksheet.getRow(currentRow);
                    subjectHeaderRow.getCell(1).value = `📚 ${subject.subject_name}`;
                    subjectHeaderRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
                    subjectHeaderRow.getCell(1).fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF4472C4' }
                    };
                    subjectHeaderRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
                    subjectHeaderRow.height = 25;
                    currentRow++;

                    // Subject summary row
                    const summaryRow = worksheet.getRow(currentRow);
                    summaryRow.getCell(1).value = 'Threshold:';
                    summaryRow.getCell(2).value = subject.threshold;
                    summaryRow.getCell(3).value = 'Total Printed:';
                    summaryRow.getCell(4).value = subject.total_printed;
                    summaryRow.getCell(5).value = 'Exceeded By:';
                    summaryRow.getCell(6).value = subject.exceeded_count;
                    
                    summaryRow.eachCell((cell, colNumber) => {
                        if (colNumber % 2 === 1) { // Label cells
                            cell.font = { bold: true, size: 10 };
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FFE7E6E6' }
                            };
                        } else { // Value cells
                            cell.font = { bold: true, size: 10 };
                            if (colNumber === 2) { // Threshold
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: 'FFFFEBEE' }
                                };
                            } else if (colNumber === 4) { // Total Printed
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: 'FFE3F2FD' }
                                };
                            } else if (colNumber === 6) { // Exceeded
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: 'FFDC3545' }
                                };
                                cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
                            }
                        }
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                    });
                    summaryRow.height = 22;
                    currentRow++;

                    // Student table header
                    const studentHeaderRow = worksheet.getRow(currentRow);
                    studentHeaderRow.getCell(1).value = 'Roll Number';
                    studentHeaderRow.getCell(2).value = 'Student Name';
                    studentHeaderRow.getCell(3).value = 'Worksheets Printed';
                    studentHeaderRow.getCell(4).value = 'Exceeded By';
                    
                    studentHeaderRow.eachCell((cell, colNumber) => {
                        if (colNumber <= 4) {
                            cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FF5B9BD5' }
                            };
                            cell.alignment = { vertical: 'middle', horizontal: 'center' };
                            cell.border = {
                                top: { style: 'thin' },
                                left: { style: 'thin' },
                                bottom: { style: 'thin' },
                                right: { style: 'thin' }
                            };
                        }
                    });
                    studentHeaderRow.height = 20;
                    currentRow++;

                    // Student rows
                    if (subject.students && subject.students.length > 0) {
                        subject.students.forEach((student, studentIndex) => {
                            const studentRow = worksheet.getRow(currentRow);
                            studentRow.getCell(1).value = student.roll_number || '---';
                            studentRow.getCell(2).value = student.student_name;
                            studentRow.getCell(3).value = student.printed;
                            studentRow.getCell(4).value = student.exceeded_by;
                            
                            studentRow.eachCell((cell, colNumber) => {
                                if (colNumber <= 4) {
                                    if (studentIndex % 2 === 0) {
                                        cell.fill = {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: 'FFFFFFFF' }
                                        };
                                    } else {
                                        cell.fill = {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: 'FFF8F9FA' }
                                        };
                                    }
                                    
                                    if (colNumber === 3 || colNumber === 4) {
                                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                                    } else {
                                        cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
                                    }
                                    
                                    if (colNumber === 4 && cell.value > 0) {
                                        cell.font = { bold: true, color: { argb: 'FFDC3545' } };
                                    }
                                    
                                    cell.border = {
                                        top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                                        left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                                        bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                                        right: { style: 'thin', color: { argb: 'FFD0D0D0' } }
                                    };
                                }
                            });
                            currentRow++;
                        });
                    } else {
                        const noDataRow = worksheet.getRow(currentRow);
                        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
                        noDataRow.getCell(1).value = 'No student data available';
                        noDataRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
                        noDataRow.getCell(1).font = { italic: true, color: { argb: 'FF6C757D' } };
                        currentRow++;
                    }

                    // Add spacing between subjects
                    currentRow++;
                });

                // Set column widths
                worksheet.getColumn(1).width = 15;
                worksheet.getColumn(2).width = 30;
                worksheet.getColumn(3).width = 20;
                worksheet.getColumn(4).width = 15;
                worksheet.getColumn(5).width = 15;
                worksheet.getColumn(6).width = 15;

                // Freeze title and first subject header
                worksheet.views = [
                    { state: 'frozen', xSplit: 0, ySplit: 1 }
                ];

                const buffer = await workbook.xlsx.writeBuffer();
                const franchiseName = franchiseData?.name || 'Franchise';
                fileDownload(buffer, `${franchiseName}_Exceeded_Threshold_${getMonthName(month)}_${year}.xlsx`);
                toast.success('Export completed successfully');
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg || 'Failed to export data');
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const columns = [
        {
            name: 'Subject',
            selector: (row) => row.subject_name,
            sortable: true,
            sortField: 'subject_name',
            wrap: true,
            minWidth: '200px',
            format: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-book" style={{ 
                        fontSize: '16px', 
                        color: '#007bff',
                        backgroundColor: '#e3f2fd',
                        padding: '8px',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}></i>
                    <span style={{ fontWeight: 'bold', fontSize: '15px', color: '#2c3e50' }}>
                        {row.subject_name}
                    </span>
                </div>
            )
        },
        {
            name: 'Threshold',
            selector: (row) => row.threshold,
            sortable: true,
            sortField: 'threshold',
            format: (row) => (
                <span style={{ 
                    backgroundColor: '#ffebee', 
                    color: '#dc3545', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                }}>
                    {row.threshold}
                </span>
            ),
            center: true,
            minWidth: '120px'
        },
        {
            name: (
                <span title="Total worksheets printed for all students (both exceeded and non-exceeded)">
                    Total Printed <i className="fas fa-info-circle" style={{ fontSize: '11px', color: '#6c757d', marginLeft: '4px' }}></i>
                </span>
            ),
            selector: (row) => row.total_printed,
            sortable: true,
            sortField: 'total_printed',
            format: (row) => (
                <span style={{ 
                    backgroundColor: '#e3f2fd', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                }}>
                    {row.total_printed}
                </span>
            ),
            center: true,
            minWidth: '150px'
        },
        {
            name: 'Exceeded By',
            selector: (row) => row.exceeded_count,
            sortable: true,
            sortField: 'exceeded_count',
            format: (row) => (
                <span style={{ 
                    backgroundColor: '#dc3545', 
                    color: '#fff', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                }}>
                    +{row.exceeded_count}
                </span>
            ),
            center: true,
            minWidth: '120px'
        }
    ];

    const ExpandedComponent = ({ data }) => {
        const bgColor = '#f1f8f4';
        const borderColor = '#4472C4';
        
        return (
            <div style={{ 
                padding: '20px 30px', 
                backgroundColor: bgColor, 
                borderTop: `3px solid ${borderColor}`,
                borderLeft: `4px solid ${borderColor}`,
                borderRight: `4px solid ${borderColor}`,
                borderBottom: `4px solid ${borderColor}`,
                margin: '0 10px 10px 10px',
                borderRadius: '0 0 8px 8px',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    gap: '15px'
                }}>
                    <h6 style={{ 
                        fontWeight: 'bold', 
                        color: '#2c3e50', 
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: 0
                    }}>
                        <i className="fas fa-users" style={{ 
                            color: borderColor,
                            backgroundColor: '#fff',
                            padding: '8px',
                            borderRadius: '50%',
                            fontSize: '14px'
                        }}></i>
                        Students who exceeded threshold in <span style={{ color: borderColor, marginLeft: '4px' }}>{data.subject_name}</span>
                    </h6>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 'bold' }}>Threshold</div>
                            <span style={{ 
                                backgroundColor: '#ffebee', 
                                color: '#dc3545', 
                                padding: '4px 12px', 
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                fontSize: '14px'
                            }}>
                                {data.threshold}
                            </span>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                                fontSize: '11px', 
                                color: '#6c757d', 
                                marginBottom: '4px', 
                                textTransform: 'uppercase', 
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                justifyContent: 'center'
                            }}>
                                Total Printed 
                                <i className="fas fa-info-circle" 
                                   style={{ fontSize: '10px', cursor: 'help' }}
                                   title="Total worksheets printed for all students (both exceeded and non-exceeded)"></i>
                            </div>
                            <span style={{ 
                                backgroundColor: '#e3f2fd', 
                                padding: '4px 12px', 
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                fontSize: '14px'
                            }}>
                                {data.total_printed}
                            </span>
                        </div>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-bordered table-sm" style={{ marginBottom: 0, backgroundColor: '#fff' }}>
                        <thead style={{ backgroundColor: '#e9ecef' }}>
                            <tr>
                                <th style={{ fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Roll Number</th>
                                <th style={{ fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Student Name</th>
                                <th style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Worksheets Printed</th>
                                <th style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Exceeded By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.students && data.students.length > 0 ? (
                                data.students.map((student, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>
                                            {student.roll_number || '---'}
                                        </td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>
                                            <b>{student.student_name}</b>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                                            <span style={{ 
                                                backgroundColor: '#e3f2fd', 
                                                padding: '4px 12px', 
                                                borderRadius: '4px',
                                                fontWeight: 'bold'
                                            }}>
                                                {student.printed}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                                            <span style={{ 
                                                backgroundColor: '#dc3545', 
                                                color: '#fff', 
                                                padding: '4px 12px', 
                                                borderRadius: '4px',
                                                fontWeight: 'bold'
                                            }}>
                                                +{student.exceeded_by}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                                        No student data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const customStyles: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontSize: '14px',
                fontWeight: 'bold'
            }
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
        rows: {
            style: {
                borderBottom: '3px solid #dee2e6',
                '&:nth-of-type(odd)': {
                    backgroundColor: '#f1f8f4',
                },
                '&:nth-of-type(even)': {
                    backgroundColor: '#e8f5e9',
                },
                '&:hover': {
                    backgroundColor: '#e3f2fd !important',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                },
                minHeight: '60px',
                fontSize: '14px',
                fontWeight: '500',
            },
        },
        cells: {
            style: {
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
    };

    const getMonthName = (monthNum) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months[parseInt(monthNum) - 1] || monthNum;
    };

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Exceeded Threshold Details</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item"><a href="/exceeded-print-threshold">Exceeded Threshold</a></li>
                                <li className="breadcrumb-item active">Details</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        Student-wise Exceeded Threshold Details
                                        {franchiseData && (
                                            <span className="ml-3" style={{ fontSize: '14px', fontWeight: 'normal', color: '#6c757d' }}>
                                                <i className="fas fa-building mr-1"></i>{franchiseData.name} - 
                                                <i className="fas fa-calendar ml-2 mr-1"></i>{getMonthName(month)} {year}
                                            </span>
                                        )}
                                    </h3>
                                    <div className="card-tools">
                                        <Button className="btn btn-success btn-sm mr-2" onClick={handleExport} isLoading={loading}>
                                            <i className="far fa-file-excel mr-1"></i> Export
                                        </Button>
                                        <Button className="btn btn-default btn-sm" onClick={backToList}>
                                            <i className="fas fa-arrow-left mr-1"></i> Back
                                        </Button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <DataTable
                                        columns={columns}
                                        data={data.map((item, index) => ({ ...item, tableRowIndex: index }))}
                                        progressPending={loading}
                                        customStyles={customStyles}
                                        highlightOnHover
                                        striped
                                        expandableRows
                                        expandableRowsComponent={ExpandedComponent}
                                        expandOnRowClicked
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ExceededThresholdDetails;
